import { ConsignmentApiModel } from '~/lib/api/smartsend/types';

/**
 * MANY_SHIPMENTS only: an arrangement that yields exactly as many shipments as the user started
 * items with has not actually rearranged anything, so there is nothing to create.
 *
 * This was previously an unnamed boolean called `valid` reported out of the parcel editor, which
 * read as a data-validity check and needed body copy to explain. It is a "you have not changed
 * anything yet" rule, so it is named for that and lives with the other rules.
 */
export const arrangementIsANoOp = (plan: ConsignmentApiModel, sourceItemCount: number): boolean =>
  plan.Parcels.length === sourceItemCount;

/**
 * Both outcomes: every content row needs a description, a positive quantity and a positive value,
 * because these rows become the customs declaration for each resulting shipment.
 *
 * Replaces three identical inline useMemos that had been copied between the split and combine flows.
 *
 * A parcel with NO rows at all counts as incomplete. Iterating `Contents` cannot catch that on its
 * own - zero rows produce zero problems - and the consequence is not cosmetic: the quote mapper
 * prices an empty parcel while the order mapper drops it, so the customer pays for N parcels and
 * ships N-1. If every parcel is empty, booking falls through to a completely different single-parcel
 * shipment. See getParcels in itemView.ts.
 */
export const contentIsIncomplete = (plan: ConsignmentApiModel): boolean =>
  (plan?.Parcels ?? []).some(
    (parcel) =>
      parcel.Contents.length === 0 ||
      parcel.Contents.some((row) => !row.Description?.trim() || !(row.Quantity > 0) || !(row.Value > 0))
  );
